var __awaiter = this && this.__awaiter || function (thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }
  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }
    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }
    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }
    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
};
/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable no-empty */
import pclnExperimentation from '@pcln/experimentation';
import { Dialog } from 'pcln-design-system';
import React, { useEffect } from 'react';
import ChatMainContentSection from './ChatContent';
import ChatMessageErrorBoundary from './components/ErrorBoundary/ChatMessageErrorBoundary';
import ErrorComponent from './components/ErrorComponent';
import { DefaultModalHeader } from './components/Header/DefaultModalHeader';
import GoogleMapComponents from './components/Maps/GoogleMapComponents';
import PennyCaresSurvey from './components/Survey/PennyCaresSurvey';
import Survey from './components/Survey/Survey';
import { DialogStyleProvider } from './context/DialogStyleProvider';
import { useChatbotState } from './hooks/useChatBotState';
import useLoadPreviousPrompts from './hooks/useLoadPreviousPrompts';
import usePromptRetrieval from './hooks/usePromptRetrieval';
import RealTimeAudioPanel from './RealTimeAudioPanel';
import { initBrowserStorage } from './utils/browserStorage';
import { isOfferSurveyDone } from './utils/pennyCaresSurvey';
import { useChatMessagesState } from './hooks/useChatMessagesState';
import { useLoadingState } from './hooks/useLoadingState';
import { useChatUIState } from './hooks/useChatUIState';
import { useRealTimeState } from './hooks/useRealTimeState';
import { useAlertDialogState } from './hooks/useAlertDialogState';
import { ChatProvider } from './context/ChatProvider';
import { DialogContentWrapper } from './components/DialogContentWrapper';
import { useSuppressOnNativeWebView } from './hooks/useSuppressOnNativeWebView';
// import { usePersonalizedGreeting } from './hooks/usePersonalizedGreeting'
export const PROMPT_LIMIT = 5;
/**
 * PclnUcChatBot with Dialog overlay
 * @public
 */
export const PclnUcChatBot = props => {
  const {
    componentVersion,
    discoveryCitySelected,
    handleMarkerResponse,
    isChatBoxOpened,
    locale = 'en-us',
    markerComponents = {},
    onChatDialogRendered,
    onClose,
    realtimeContext,
    resolvedConfig,
    resolvedDisplay,
    resolvedFeatures,
    resolvedPayload
  } = props;
  // Suppress component if rendered on a native webview
  const {
    shouldSuppress
  } = useSuppressOnNativeWebView();
  if (shouldSuppress) {
    return null;
  }
  // Config and service related
  const {
    requestFrom,
    debugMode,
    fetchRequestOptions,
    retrieveUrl
  } = resolvedConfig !== null && resolvedConfig !== void 0 ? resolvedConfig : {};
  const browserStorage = initBrowserStorage(requestFrom);
  // Feature flags and settings
  const {
    setiValues = {},
    quickActionsStackVariant,
    isSurveyEligible,
    isCaresSurveyEligible
  } = resolvedFeatures !== null && resolvedFeatures !== void 0 ? resolvedFeatures : {};
  const {
    isRealtime,
    isCaresSurveyEnabled
  } = setiValues;
  // Display configuration
  const {
    chatContainerHeight,
    dialogOverlayProps
  } = resolvedDisplay !== null && resolvedDisplay !== void 0 ? resolvedDisplay : {};
  // Payload and survey related
  const {
    isMobile,
    submitChatMessagesPayload
  } = resolvedPayload;
  const {
    productId,
    cguid,
    hotelPayload,
    isSessionActive
  } = submitChatMessagesPayload;
  const offerNumber = hotelPayload === null || hotelPayload === void 0 ? void 0 : hotelPayload.offerNumber;
  const pennyCareSurveyDone = typeof window !== 'undefined' ? isOfferSurveyDone(offerNumber) : false;
  const isCaresSurveyEligibleAndEnabled = isCaresSurveyEligible && isCaresSurveyEnabled && !pennyCareSurveyDone;
  const {
    logAnalytics,
    speechFetchRequestOptions
  } = resolvedConfig || {};
  const {
    logChatInitialization,
    logCustomerCarePhoneNumberClicked,
    logCustomerCarePhoneNumberDisplayed,
    logFirstMessageSubmitted
  } = logAnalytics;
  // Create state for chat messages
  const {
    chatMessages,
    setChatMessages,
    santizedSubmitChatMessagesPayload,
    setSanitizedSubmitChatMessagesPayload
  } = useChatMessagesState({
    submitChatMessagesPayload,
    resolvedFeatures
  });
  // Create state for Loading related values
  const {
    isLoading,
    setLoadingStatus,
    retrievalLoading,
    setRetrievalLoading,
    loadingStatusMessages,
    setLoadingStatusMessage
  } = useLoadingState();
  // Create state for realtime related values
  const {
    startRealTime,
    setStartRealTime
  } = useRealTimeState({
    isRealtime
  });
  const isAlertDialogActive = useAlertDialogState();
  // Create state for UI related values
  const {
    showSurvey,
    setShowSurvey,
    openErrorComponent,
    setOpenErrorComponent,
    isMapOpened,
    setIsMapOpened
  } = useChatUIState();
  const {
    audioPlayerRef,
    loadMoreMessages,
    setLoadMoreMessages,
    lastPromptId,
    setLastPromptId,
    telemetrySessionId
  } = useChatbotState(Object.assign(Object.assign({}, props), {
    submitChatMessagesPayload
  }));
  // Set telemetry session ID outside of effects
  submitChatMessagesPayload.telemetrySessionId = telemetrySessionId;
  const visibilityChannel = new BroadcastChannel('chatbot-session-events');
  // New Speech Stuff for UXPT-6916
  const enableVoice = setiValues === null || setiValues === void 0 ? void 0 : setiValues.enableVoice;
  const enableVoiceTooltip = setiValues === null || setiValues === void 0 ? void 0 : setiValues.enableVoiceTooltip;
  useEffect(() => {
    const handleMessage = event => {
      const {
        count
      } = event.data;
      if (browserStorage) {
        browserStorage.updateUserMessageCount(count);
        if (count > 0) {
          browserStorage.setInitialMessageSubmitted();
        }
      }
    };
    visibilityChannel.addEventListener('message', handleMessage);
    return () => {
      visibilityChannel.removeEventListener('message', handleMessage);
    };
  }, []);
  useEffect(() => {
    const updateMessageCountAsync = () => __awaiter(void 0, void 0, void 0, function* () {
      const userChatMessagesCount = chatMessages.filter(message => message.role === 'user' && message.content !== '').length;
      visibilityChannel.postMessage({
        count: userChatMessagesCount
      });
    });
    updateMessageCountAsync().catch(e => {});
  }, [chatMessages]);
  // // Add personalized greeting hook
  // const { personalizedGreetingMessage, isLoadingGreetingMessage: isPersonalizedGreetingLoading } = usePersonalizedGreeting(
  //   setiValues?.shouldDisplayPersonalizedGreeting,
  //   submitChatMessagesPayload,
  //   resolvedConfig?.personalizedGreetingUrl || '',
  //   isChatBoxOpened,
  // )
  // // Add personalized greeting message to chat when received
  // useEffect(() => {
  //   if (resolvedFeatures.setiValues.shouldDisplayPersonalizedGreeting && personalizedGreetingMessage) {
  //     const greetingMessage = {
  //       role: 'assistant',
  //       content: personalizedGreetingMessage,
  //       displayComponentName: 'SESSION_INITIALIZED',
  //       date: new Date(),
  //     }
  //     setChatMessages([greetingMessage])
  //   }
  // }, [personalizedGreetingMessage])
  useEffect(() => {
    if (isChatBoxOpened) {
      logChatInitialization();
      onChatDialogRendered();
      if (!(resolvedConfig === null || resolvedConfig === void 0 ? void 0 : resolvedConfig.customerName) && requestFrom === 'homePage') {
        pclnExperimentation.fireImpression('CHATBOT_VOICE_REALTIME_SIGNED_OUT');
      }
    }
  }, [isChatBoxOpened, resolvedConfig === null || resolvedConfig === void 0 ? void 0 : resolvedConfig.customerName, onChatDialogRendered]);
  usePromptRetrieval({
    retrieveUrl,
    cguid,
    lastPromptId,
    setChatMessages,
    setRetrievalLoading,
    setLastPromptId,
    limit: PROMPT_LIMIT,
    isSessionActive: isSessionActive !== null && isSessionActive !== void 0 ? isSessionActive : true,
    productId,
    requestFrom,
    chatMessages
  });
  useLoadPreviousPrompts({
    retrieveUrl,
    cguid,
    lastPromptId,
    limit: PROMPT_LIMIT,
    loadMoreMessages,
    setChatMessages,
    setRetrievalLoading,
    setLastPromptId,
    setLoadMoreMessages,
    isSessionActive: isSessionActive !== null && isSessionActive !== void 0 ? isSessionActive : true
  });
  const resolveSurvey = () => {
    if (isCaresSurveyEligibleAndEnabled) {
      return React.createElement(PennyCaresSurvey, {
        onClose: onClose,
        offerNumber: offerNumber
      });
    }
    return React.createElement(Survey, {
      onClose: function () {
        setShowSurvey(false);
        onClose();
      },
      requestFrom: requestFrom
    });
  };
  return React.createElement(ChatProvider, {
    chatbotData: {
      audioPlayerRef,
      chatContainerHeight,
      componentVersion,
      debugMode,
      discoveryCitySelected,
      enableVoice,
      enableVoiceTooltip,
      fetchRequestOptions,
      handleMarkerResponse,
      isDev: resolvedConfig === null || resolvedConfig === void 0 ? void 0 : resolvedConfig.isDev,
      isMobile,
      isRealtime,
      isSessionActive: true,
      loadMoreMessages,
      locale,
      logCustomerCarePhoneNumberClicked,
      logCustomerCarePhoneNumberDisplayed,
      logFirstMessageSubmitted,
      markerComponents,
      quickActionsStackVariant,
      requestFrom,
      setiValues,
      setLoadMoreMessages,
      shouldDisplayGenHotelFeature: setiValues === null || setiValues === void 0 ? void 0 : setiValues.shouldDisplayGenHotelFeature,
      speechFetchRequestOptions,
      submitChatMessagesPayload: santizedSubmitChatMessagesPayload,
      transcriptionFetchRequestOptions: (resolvedConfig === null || resolvedConfig === void 0 ? void 0 : resolvedConfig.transcriptionFetchRequestOptions) || {}
    },
    chatMessages: {
      chatMessages,
      setChatMessages,
      santizedSubmitChatMessagesPayload,
      setSanitizedSubmitChatMessagesPayload
    },
    loadingState: {
      isLoading,
      setLoadingStatus,
      retrievalLoading,
      setRetrievalLoading,
      loadingStatusMessages,
      setLoadingStatusMessage
    },
    ui: {
      showSurvey,
      setShowSurvey,
      openErrorComponent,
      setOpenErrorComponent,
      isMapOpened,
      setIsMapOpened,
      shouldDisplayMapButton: true
    },
    realtime: {
      setStartRealTime,
      startRealTime,
      isRealtime
    }
  }, React.createElement(DialogStyleProvider, null, React.createElement("audio", {
    id: 'penny-audio-player',
    "data-testid": 'penny-audio-player',
    ref: audioPlayerRef
  }, React.createElement("track", {
    kind: 'captions'
  })), React.createElement(Dialog, Object.assign({
    showCloseButton: false,
    scrimDismiss: !startRealTime,
    open: isChatBoxOpened,
    size: 'lg',
    onOpenChange: function (isOpen) {
      if (!isOpen) {
        onClose();
      }
    },
    borderRadius: isMobile ? 'lg' : '2xl',
    shouldDisableTabIndex: true
  }, dialogOverlayProps), React.createElement(DialogContentWrapper, Object.assign({
    isAlertDialogActive: isAlertDialogActive
  }, dialogOverlayProps, {
    chatContainerHeight: chatContainerHeight
  }), startRealTime ? React.createElement(RealTimeAudioPanel, {
    locale: locale,
    realtimeContext: realtimeContext,
    isModal: true,
    requestFrom: requestFrom
  }) : React.createElement(React.Fragment, null, React.createElement(DefaultModalHeader, {
    onClose: function () {
      if (isSurveyEligible && !showSurvey && (setiValues === null || setiValues === void 0 ? void 0 : setiValues.shouldDisplaySurvey) || !showSurvey && isCaresSurveyEligibleAndEnabled) {
        setShowSurvey(true);
      } else {
        setShowSurvey(false);
        onClose();
      }
    },
    speechEnabled: setiValues === null || setiValues === void 0 ? void 0 : setiValues.enableVoice
  }), !isMapOpened ?
  // display Chat Component
  React.createElement(ChatMessageErrorBoundary, {
    FallbackComponent: React.createElement(ErrorComponent, null)
  }, (setiValues === null || setiValues === void 0 ? void 0 : setiValues.shouldDisplaySurvey) && showSurvey || isCaresSurveyEligibleAndEnabled && showSurvey ? resolveSurvey() : React.createElement(ChatMainContentSection, {
    retrievalLoading: retrievalLoading,
    visibilityChannel: visibilityChannel
  })) :
  // display Maps Component
  React.createElement(ChatMessageErrorBoundary, {
    compnentName: 'GoogleMapComponents',
    FallbackComponent: React.createElement(ErrorComponent, null)
  }, React.createElement(GoogleMapComponents, null)))))));
};